<template>

  <report-panel icon="$revenue" :title="$t('titles.gross_revenue_by_channel')" :loading="loading">
  
    <div ref="chartContainer">
      <bar
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import { currencyFormatter, numberFormatter } from '../../../../utils/formatters'
import ChannelRegistry from '../../../../channels/ChannelRegistry'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Filler,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  type ChartOptions, type ChartData, type TooltipItem, type ChartDataset, type LegendItem
} from 'chart.js'
import type { ReportingDateSummaries } from '../../../../services/api-clients/ReportingApi'

ChartJS.register(Filler, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface Props {
  dateSummaries: ReportingDateSummaries
  loading: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const { d } = useI18n()

/**
 * Chart
 */

const chartOptions: ChartOptions<'bar'> = {
  interaction: {
    intersect: false, // show the tooltip when the user mouses over any part of the area, not just the data point itself
  },
  // aspectRatio: 16/9,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        generateLabels(chart: ChartJS): LegendItem[] {
          return chart.data.datasets.filter(dataset => dataset.stack === 'revenue').map((dataset, i) => {
            const item: LegendItem = {
              text: dataset.label ?? '?"',
              datasetIndex: i,
              fillStyle: (dataset.backgroundColor as string),
              hidden: !chart.isDatasetVisible(i),
            }
            return item
          })
        }
      }
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          if (context.dataset.stack !== 'revenue') {
            return ''
          }
          const channel = context.dataset.label
          const amount = currencyFormatter.format(context.raw as number)
          const countDataset = context.chart.data.datasets.find(d => d.stack === 'count' && d.label === context.dataset.label)
          const count = countDataset ? countDataset.data[context.dataIndex] : '?'
          return `${channel}: ${amount} (${count})`
        },
      },
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      ticks: {
        callback: (value, _index, _values) => {
          return `$${numberFormatter.format(value as number)}`
        },
      },
    },
  },
}

const chartData = computed<ChartData<'bar', number[]>>(() => {

  const revenueDatasets: {[key: string]: ChartDataset<'bar', number[]>} = {}
  const countDatasets: {[key: string]: ChartDataset<'bar', number[]>} = {}

  Object.values(props.dateSummaries).forEach((summary) => {
    Object.keys(summary.as_transaction_date.gross_revenue.by_channel).forEach((channelName) => {

      const channel = ChannelRegistry.find(channelName)

      revenueDatasets[channelName] = {
        label: channel?.titleShort ?? channelName,
        type: 'bar',
        backgroundColor: channel?.color ?? '#787878',
        data: [],
        stack: 'revenue',
      }

      countDatasets[channelName] = {
        label: channel?.titleShort ?? channelName,
        type: 'bar',
        backgroundColor: channel?.color ?? '#787878',
        data: [],
        stack: 'count',
        hidden: true,
      }

    })
  })

  Object.values(props.dateSummaries).forEach((summary) => {
    Object.keys(revenueDatasets).forEach(channelName => {
      
      let revenueDatum = 0
      if (channelName in summary.as_transaction_date.gross_revenue.by_channel) {
        revenueDatum = summary.as_transaction_date.gross_revenue.by_channel[channelName]
      }
      revenueDatasets[channelName].data.push(revenueDatum)

      let countDatum = 0
      if (channelName in summary.as_transaction_date.transactions.by_channel) {
        countDatum = summary.as_transaction_date.transactions.by_channel[channelName]
      }
      countDatasets[channelName].data.push(countDatum)

    })
  })

  return {
    labels: Object.keys(props.dateSummaries).map((dateString) => {
      return d(dateString, 'month_day_numeric')
    }),
    datasets: [...Object.values(revenueDatasets), ...Object.values(countDatasets)],
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const chartContainer = useTemplateRef('chartContainer')
const { chartStyles } = useFitChartToContainer(chartContainer)

</script>
